import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Code,
  VStack,
  Divider,
  Container,
  Button,
  HStack,
  useToast,
  Link,
  AspectRatio,
  List,
  ListItem,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

const concepts = [
  {
    title: "1. What is React? Overview, Roadmap & Prerequisites",
    descriptionSections: [
      {
        text: "React.js (often called React) is a free and open-source JavaScript library, maintained by Meta, for building interactive, component-based user interfaces (UIs). It is widely used for creating single-page applications (SPAs) and is renowned for its efficiency and flexible architecture."
      },
      {
        title: "Overview of React",
        points: [
          "Declarative UI: React efficiently updates and renders only the necessary components when your data changes.",
          "Component-based architecture: Build apps with reusable, encapsulated components.",
          "Virtual DOM: React computes efficient DOM updates for better performance.",
          "JSX: Syntax extension that looks like HTML inside JavaScript.",
          "Hooks: Functions like useState and useEffect to manage state and lifecycle in functional components.",
          "Unidirectional data flow: Props flow from parent to child components making apps predictable and easier to debug."
        ]
      },
      {
        title: "React ecosystem",
        points: [
          "React frameworks: Next.js, Remix for SSR/SSG and routing.",
          "Routing: React Router for client-side navigation.",
          "State management: Redux, Zustand for global state.",
          "Styling: CSS Modules, Styled Components, Tailwind CSS.",
          "Build tools: Webpack, Babel to bundle and transpile JSX.",
          "Mobile apps: React Native for iOS and Android development."
        ]
      }
    ],
    code: `// Minimal React entry example
import React from "react";
import { createRoot } from "react-dom/client";
function App() { return <h1>Hello from React!</h1>; }
createRoot(document.getElementById("root")).render(<App />);`,
    resourceUrl: "https://react.dev/learn/getting-started",
    youtube: "https://www.youtube.com/embed/FNnhEBDYBr8",
  },

  {
    title: "2. JSX & Rendering (Basics)",
    descriptionSections: [
      {
        text: "JSX stands for JavaScript XML. It is a syntax extension for JavaScript that allows you to write HTML-like code directly within your JavaScript files. While JSX resembles HTML, it is not actual HTML. Instead, it is a declarative way to describe the structure of your UI components."
      },
      {
        title: "Key characteristics of JSX",
        points: [
          "HTML-like syntax: Uses tags and attributes similar to HTML, making it intuitive for web developers.",
          "Embedded JavaScript expressions: Use curly braces {} to embed JS expressions for dynamic content and logic.",
          "Transpilation: Browsers do not understand JSX directly; it must be transpiled with Babel into regular JS.",
          "Single root element: A component must return a single root JSX element; wrap multiple elements in a div or React.Fragment."
        ]
      },
      {
        title: "Rendering",
        text: "Rendering in React refers to displaying UI components on the screen. React uses ReactDOM.render() to achieve this in web applications."
      },
      {
        title: "How rendering works",
        points: [
          "JSX to React Elements: JSX is converted into lightweight JS objects called React elements that describe the UI.",
          "Virtual DOM: React keeps a virtual DOM in memory to efficiently calculate changes.",
          "Efficient Updates: React computes minimal changes needed and applies them to the real DOM.",
          "Real DOM Update: React applies optimized changes, making UI updates fast."
        ]
      },
      {
        title: "ReactDOM.render()",
        text: "This function renders a React application into the browser's DOM. It takes two arguments: the JSX element (or component) to render and the target DOM element to mount the app."
      },
      {
        title: "Example",
        code: `import React from 'react';
import ReactDOM from 'react-dom';

const App = () => {
  return <h1>Hello, React!</h1>;
};

ReactDOM.render(<App />, document.getElementById('root'));`
      }
    ],
    code: `import React from 'react';
import ReactDOM from 'react-dom';

const App = () => {
  return <h1>Hello, React!</h1>;
};

ReactDOM.render(<App />, document.getElementById('root'));`,
    resourceUrl: "https://react.dev/learn/writing-markup-with-jsx",
    youtube: "https://www.youtube.com/embed/KgAN353TY-8",
  },

  {
    title: "3. Components (Functional vs Class) & Composition",
    descriptionSections: [
      {
        text: "React components are the fundamental building blocks of React applications, encapsulating UI logic and rendering. They can be created as either functional components or class components, and their design often involves composition."
      },
      {
        title: "Functional Components",
        points: [
          "Definition: Plain JavaScript functions that accept props as an argument and return React elements (JSX) to describe what should be rendered.",
          "Simplicity: Generally simpler and more concise than class components, ideal for presentational components that don't manage complex state.",
          "Hooks: With React Hooks (useState, useEffect, useContext), functional components can now manage state and handle side effects."
        ],
        code: `function Greeting(props) {
  return <h1>Hello, {props.name}!</h1>;
}`
      },
      {
        title: "Class Components",
        points: [
          "Definition: ES6 classes that extend React.Component, have a render() method, and can manage internal state using this.state and this.setState().",
          "Lifecycle Methods: Provide methods like componentDidMount, componentDidUpdate, and componentWillUnmount for performing actions at specific lifecycle phases.",
          "Legacy Use Cases: Still supported but less common due to Hooks. Used mainly for error boundaries or older codebases."
        ],
        code: `class Counter extends React.Component {
  constructor(props) {
    super(props);
    this.state = { count: 0 };
  }

  render() {
    return (
      <div>
        <p>Count: {this.state.count}</p>
        <button onClick={() => this.setState({ count: this.state.count + 1 })}>
          Increment
        </button>
      </div>
    );
  }
}`
      },
      {
        title: "Composition",
        points: [
          "Principle: React emphasizes composition over inheritance for reusing UI code.",
          "Benefits: Promotes reusability, maintainability, and testability as each component has a clear responsibility.",
          "Implementation: Achieved by passing components as props, rendering them in JSX, or using higher-order components (HOCs) or render props patterns."
        ],
        code: `function Card(props) {
  return (
    <div className="card">
      {props.children} {/* Renders content passed as children */}
    </div>
  );
}

function UserProfile() {
  return (
    <Card>
      <h2>John Doe</h2>
      <p>Software Engineer</p>
    </Card>
  );
}`
      }
    ],
    code: `function Card(props) {
  return (
    <div className="card">
      {props.children} {/* Renders content passed as children */}
    </div>
  );
}

function UserProfile() {
  return (
    <Card>
      <h2>John Doe</h2>
      <p>Software Engineer</p>
    </Card>
  );
}`,
    resourceUrl: "https://react.dev/learn/your-first-component",
    youtube: "https://www.youtube.com/embed/Mxh1fQXrjhA",
  },

  {
    title: "4. Props (Passing Data) & Prop Drilling",
    descriptionSections: [
      {
        title: "Overview",
        text: "Props are the way parent components pass data to children. Avoid deep prop drilling using Context."
      },
      {
        title: "Key Points",
        points: [
          "Props are read-only inside child components.",
          "Prop drilling: passing props through multiple levels can become cumbersome.",
          "Use Context API to provide values deep in the component tree without manual drilling."
        ]
      }
    ],
    code: `function Parent() {
  return <Child message="Hi!" />;
}
function Child({ message }) {
  return <p>{message}</p>;
}`,
    resourceUrl: "https://react.dev/learn/passing-props-to-a-component",
    youtube: "https://www.youtube.com/embed/pFwb_iJVolc",
  },

  {
    title: "5. State & Hooks — useState",
    descriptionSections: [
      {
        title: "Overview",
        text: "State allows components to remember information and respond to user interactions. Hooks let functional components manage state and lifecycle."
      },
      {
        title: "Key Points",
        points: [
          "useState hook declares component-local state.",
          "State updates with setter functions and can trigger re-renders.",
          "Avoid stale closures; use functional updates when depending on previous state."
        ]
      }
    ],
    code: `import React, { useState } from "react";
function Counter() {
  const [count, setCount] = useState(0);
  return <button onClick={() => setCount(c => c + 1)}>Count: {count}</button>;
}`,
    resourceUrl: "https://react.dev/learn/state-a-components-memory",
    youtube: "https://www.youtube.com/embed/O6P86uwfdR0",
  },

  {
    title: "6. Handling Events & Controlled Inputs",
    descriptionSections: [
      {
        title: "Overview",
        text: "React events are camelCase and can be controlled (state-driven) or uncontrolled (DOM-driven)."
      },
      {
        title: "Key Points",
        points: [
          "Event pooling: Synthetic events wrap native events for consistency.",
          "Controlled components have state as single source of truth.",
          "Forms: use onChange to update state; prevent default for submit events."
        ]
      }
    ],
    code: `function NameForm() {
  const [name, setName] = useState("");
  return (
    <form onSubmit={e => { e.preventDefault(); alert(name); }}>
      <input value={name} onChange={e => setName(e.target.value)} />
    </form>
  );
}`,
    resourceUrl: "https://react.dev/learn/responding-to-events",
    youtube: "https://www.youtube.com/embed/SpDG283b4bw",
  },

  {
    title: "7. Conditional Rendering, Lists & Keys",
    descriptionSections: [
      {
        title: "Overview",
        text: "Render UI conditionally and handle lists efficiently with unique keys."
      },
      {
        title: "Key Points",
        points: [
          "Conditional rendering: use ternary operators or logical &&.",
          "Lists: map arrays to JSX elements.",
          "Keys: ensure unique keys for stable rendering performance."
        ]
      }
    ],
    code: `function NumberList({ nums }) {
  return <ul>{nums.map(n => <li key={n}>{n}</li>)}</ul>;
}`,
    resourceUrl: "https://react.dev/learn/conditional-rendering",
    youtube: "https://www.youtube.com/embed/rQyB5yA13gE",
  },

  {
    title: "8. Side Effects with useEffect",
    descriptionSections: [
      {
        title: "Overview",
        text: "useEffect manages side effects like data fetching, subscriptions, and timers in functional components."
      },
      {
        title: "Key Points",
        points: [
          "Dependency array controls when effect runs.",
          "Return a cleanup function to avoid memory leaks.",
          "Can mimic componentDidMount, componentDidUpdate, componentWillUnmount."
        ]
      }
    ],
    code: `useEffect(() => {
  const id = setInterval(() => console.log('tick'), 1000);
  return () => clearInterval(id);
}, []);`,
    resourceUrl: "https://react.dev/learn/synchronizing-with-effects",
    youtube: "https://www.youtube.com/embed/0ZJgIjIuY7U",
  },

  {
    title: "9. Routing with React Router (v6)",
    descriptionSections: [
      {
        title: "Overview",
        text: "React Router enables client-side navigation in single-page applications (SPA)."
      },
      {
        title: "Key Points",
        points: [
          "BrowserRouter wraps the app for history management.",
          "Routes define paths and corresponding elements.",
          "Link replaces <a> for client-side navigation.",
          "Nested routes allow hierarchical layouts."
        ]
      }
    ],
    code: `import { BrowserRouter, Routes, Route, Link } from "react-router-dom";

function App() {
  return (
    <BrowserRouter>
      <nav><Link to='/'>Home</Link></nav>
      <Routes>
        <Route path='/' element={<Home />} />
        <Route path='/about' element={<About />} />
      </Routes>
    </BrowserRouter>
  );
}`,
    resourceUrl: "https://reactrouter.com/en/main",
    youtube: "https://www.youtube.com/embed/bz7YmXXM2Z8",
  },

  {
    title: "10. Context API (Avoid Prop-Drilling)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Context provides a way to pass values deeply without prop drilling."
      },
      {
        title: "Key Points",
        points: [
          "Create context with React.createContext.",
          "Provide values using Context.Provider.",
          "Consume using useContext hook inside child components."
        ]
      }
    ],
    code: `const ThemeContext = React.createContext('light');
function App() {
  return <ThemeContext.Provider value="dark"><Child /></ThemeContext.Provider>;
}`,
    resourceUrl: "https://react.dev/learn/passing-data-deeply-with-context",
    youtube: "https://www.youtube.com/embed/5v1NUQbB6g4",
  },
  {
    title: "11. useReducer & Building Custom Hooks",
    descriptionSections: [
      {
        title: "Overview",
        text: "useReducer provides a way to manage complex state transitions. Custom hooks let you encapsulate logic and reuse it across components."
      },
      {
        title: "Key Points",
        points: [
          "useReducer is ideal for complex state logic instead of multiple useState calls.",
          "Custom hooks: functions prefixed with 'use' to encapsulate reusable logic.",
          "Encapsulate effects, state, or event handling inside custom hooks."
        ]
      }
    ],
    code: `function reducer(state, action) {
  switch(action.type) {
    case 'inc': return {count: state.count + 1};
    default: return state;
  }
}
function Counter(){ 
  const [s, d] = useReducer(reducer, {count:0}); 
  return <button onClick={()=>d({type:'inc'})}>{s.count}</button> 
}`,
    resourceUrl: "https://react.dev/reference/react/useReducer",
    youtube: "https://www.youtube.com/embed/kK_Wqx3RnHk",
  },

  {
    title: "12. Forms & Validation (React Hook Form / Formik)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Forms in React can be controlled or uncontrolled. Use libraries like React Hook Form or Formik for scalable validation."
      },
      {
        title: "Key Points",
        points: [
          "Controlled components tie input values to component state.",
          "Validation strategies include schema-based (Yup) or custom logic.",
          "React Hook Form simplifies form state management and performance."
        ]
      }
    ],
    code: `function EmailForm() {
  const [email, setEmail] = useState('');
  return <input value={email} onChange={e => setEmail(e.target.value)} />;
}`,
    resourceUrl: "https://react.dev/learn/forms",
    youtube: "https://www.youtube.com/embed/Mxh1fQXrjhA",
  },

  {
    title: "13. Performance Optimization (memo, useMemo, useCallback)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Optimize React apps to avoid unnecessary re-renders and expensive computations."
      },
      {
        title: "Key Points",
        points: [
          "React.memo prevents functional components from re-rendering unnecessarily.",
          "useMemo caches expensive computations.",
          "useCallback caches functions to prevent child re-render triggers.",
          "Consider virtualization for long lists."
        ]
      }
    ],
    code: `const Heavy = React.memo(({data}) => /* render */ null);
const derived = useMemo(()=> expensiveCalc(list), [list]);`,
    resourceUrl: "https://react.dev/learn/escape-hatches",
    youtube: "https://www.youtube.com/embed/0oXYLzuucwE",
  },

  {
    title: "14. Code Splitting & Lazy Loading",
    descriptionSections: [
      {
        title: "Overview",
        text: "Reduce initial bundle size by splitting code and loading components on demand."
      },
      {
        title: "Key Points",
        points: [
          "React.lazy dynamically imports components.",
          "Suspense displays fallback UI during loading.",
          "Route-based splitting improves initial page load."
        ]
      }
    ],
    code: `const LazyComp = React.lazy(() => import('./Big'));
<Suspense fallback={<div>Loading...</div>}><LazyComp/></Suspense>`,
    resourceUrl: "https://react.dev/learn/code-splitting",
    youtube: "https://www.youtube.com/embed/JOYcdi8GI4",
  },

  {
    title: "15. State Management: Context vs Redux vs Zustand",
    descriptionSections: [
      {
        title: "Overview",
        text: "Manage global state using appropriate tools depending on app complexity."
      },
      {
        title: "Key Points",
        points: [
          "Context is suitable for low-frequency state updates.",
          "Redux provides predictable global state with strict patterns.",
          "Zustand is simpler, minimal boilerplate, and highly performant."
        ]
      }
    ],
    code: `import create from 'zustand';
const useStore = create(set => ({ 
  count:0, 
  inc:()=>set(s=>({count:s.count+1})) 
}));`,
    resourceUrl: "https://redux.js.org/introduction/why-should-i-use-redux",
    youtube: "https://www.youtube.com/embed/x4rFhThSX04",
  },

  {
    title: "16. Testing React (Jest + React Testing Library)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Testing ensures component behavior remains correct as your app evolves."
      },
      {
        title: "Key Points",
        points: [
          "Render components and assert output with React Testing Library.",
          "Simulate user interactions with user-event library.",
          "Mock network requests and external dependencies.",
          "Unit & integration tests for maintainable suites."
        ]
      }
    ],
    code: `import { render, screen } from '@testing-library/react';
test('renders title', () => {
  render(<App />);
  expect(screen.getByText(/hello/i)).toBeInTheDocument();
});`,
    resourceUrl: "https://testing-library.com/docs/react-testing-library/intro",
    youtube: "https://www.youtube.com/embed/Ke90Tje7VS0",
  },

  {
    title: "17. Server-Side Rendering & Next.js (Intro)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Next.js enables SSR/SSG for performance, SEO, and full-featured React apps."
      },
      {
        title: "Key Points",
        points: [
          "File-based routing with pages folder.",
          "getStaticProps / getServerSideProps for data fetching.",
          "SSR improves SEO; SSG pre-builds pages for performance."
        ]
      }
    ],
    code: `// pages/index.js (Next.js)
export default function Home({data}) { return <div>{data.title}</div> }
export async function getStaticProps(){ return {props:{data:{title:'Hi'}}} }`,
    resourceUrl: "https://nextjs.org/learn/basics/create-nextjs-app",
    youtube: "https://www.youtube.com/embed/CgkZ7MvWUAA",
  },

  {
    title: "18. TypeScript with React (Basics)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Use TypeScript to add static type safety to your React components."
      },
      {
        title: "Key Points",
        points: [
          "Type props and component state.",
          "Use FC (FunctionComponent) type or generics.",
          "Type custom hooks and refs for better reliability."
        ]
      }
    ],
    code: `type Props = { title: string };
function Header({title}: Props) { return <h1>{title}</h1> }`,
    resourceUrl: "https://react.dev/learn/react-with-typescript",
    youtube: "https://www.youtube.com/embed/hQAHSlTtcmY",
  },

  {
    title: "19. React Internals (Fiber & Reconciliation)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Understand how React efficiently updates the DOM and schedules rendering."
      },
      {
        title: "Key Points",
        points: [
          "Virtual DOM for efficient diffing and updates.",
          "Fiber architecture allows interruptible rendering and prioritization.",
          "Reconciliation ensures minimal DOM mutations."
        ]
      }
    ],
    code: `// Conceptual snippet: study scheduling and reconciliation algorithms`,
    resourceUrl: "https://react.dev/learn/react-glossary#reconciliation",
    youtube: "https://www.youtube.com/embed/1z-E_KOC2L0",
  },

  {
    title: "20. Concurrent Mode, Suspense & Transitions",
    descriptionSections: [
      {
        title: "Overview",
        text: "Concurrent React enables interruptible rendering for smoother UIs."
      },
      {
        title: "Key Points",
        points: [
          "Suspense handles lazy-loaded components gracefully.",
          "useTransition lets you defer updates without blocking UI.",
          "Concurrent features improve responsiveness in heavy apps."
        ]
      }
    ],
    code: `<Suspense fallback={<Spinner/>}><SlowComponent/></Suspense>`,
    resourceUrl: "https://react.dev/learn/concurrent-mode-intro",
    youtube: "https://www.youtube.com/embed/UVhIMwHDS7k",
  },

  {
    title: "21. Advanced Patterns (Compound Components, Render Props, HOCs)",
    descriptionSections: [
      {
        title: "Overview",
        text: "Advanced component patterns improve reusability and flexibility."
      },
      {
        title: "Key Points",
        points: [
          "Compound components share context internally for grouped components.",
          "Render props allow passing a function to dynamically render UI.",
          "Higher-order components (HOCs) enhance components with extra functionality."
        ]
      }
    ],
    code: `// Compound component concept
function Tabs({children}){ /* provide context to TabList/TabPanels */ }`,
    resourceUrl: "https://epicreact.dev/advanced-patterns/",
    youtube: "https://www.youtube.com/embed/1lPrtceM6QQ",
  },

  {
    title: "22. Architecting Large React Apps & Deployment",
    descriptionSections: [
      {
        title: "Overview",
        text: "Organize large React apps with scalable structure and deploy efficiently."
      },
      {
        title: "Key Points",
        points: [
          "Folder structure: separate features, components, hooks.",
          "Performance budgets and code splitting for large apps.",
          "Deployment: Vercel, Netlify, static hosting, or containerized apps."
        ]
      }
    ],
    code: `// Example: production build command
"build": "react-scripts build"`,
    resourceUrl: "https://react.dev/learn/optimizing-performance",
    youtube: "https://www.youtube.com/embed/hQAHSlTtcmY",
  }
];

const LearnReact = () => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const toast = useToast();
  const navigate = useNavigate();

  const handleNext = () => {
    if (currentChapter < concepts.length - 1) {
      setCurrentChapter(currentChapter + 1);
    } else {
      toast({
        title: "Completed Chapters",
        description: "You've reached the end of the current curriculum.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePrevious = () => {
    if (currentChapter > 0) setCurrentChapter(currentChapter - 1);
  };

  const handleStartTest = () => {
    navigate("/test-react");
  };

  const currentConcept = concepts[currentChapter];

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="2xl" textAlign="center">
          React.js Learning Path — Basics → Expert
        </Heading>
        <Text fontSize="lg" textAlign="center" color="gray.600">
          Short focused videos (≈10–25m) + official resources for each chapter.
        </Text>

        <Divider />

        <Box p={6} shadow="md" borderWidth="1px" borderRadius="md" minH="520px">
          <Heading as="h2" size="lg" mb={2}>
            {currentConcept.title}
          </Heading>

          {/* Render Description Sections */}
          {currentConcept.descriptionSections.map((section, idx) => (
            <Box key={idx} mb={4}>
              {section.title && (
                <Text fontWeight="bold" fontSize="md" mb={2}>
                  {section.title}
                </Text>
              )}
              {section.text && <Text mb={2}>{section.text}</Text>}
              {section.points && (
                <List spacing={2} pl={4} styleType="disc">
                  {section.points.map((point, i) => (
                    <ListItem key={i}>{point}</ListItem>
                  ))}
                </List>
              )}
            </Box>
          ))}

          {/* Code Snippet */}
          <Box bg="gray.800" p={4} borderRadius="md" overflowX="auto" mb={4}>
            <Code colorScheme="cyan" whiteSpace="pre-wrap">
              {currentConcept.code}
            </Code>
          </Box>

          {/* Resources & Video */}
          <Box>
            <Heading as="h3" size="md" mb={2}>
              📚 Resources
            </Heading>
            <Link
              href={currentConcept.resourceUrl}
              color="teal.400"
              isExternal
              display="block"
              mb={2}
            >
              Read Official / Free Reference
            </Link>

            <AspectRatio ratio={16 / 9} maxW="800px">
              <iframe
                title={currentConcept.title}
                src={currentConcept.youtube}
                allowFullScreen
              />
            </AspectRatio>
          </Box>
        </Box>

        <HStack justifyContent="space-between">
          <Button
            onClick={handlePrevious}
            isDisabled={currentChapter === 0}
            variant="outline"
            colorScheme="teal"
          >
            Previous
          </Button>

          <Text>
            Chapter {currentChapter + 1} of {concepts.length}
          </Text>

          {currentChapter === concepts.length - 1 ? (
            <Button colorScheme="orange" onClick={handleStartTest}>
              🚀 Start Test
            </Button>
          ) : (
            <Button onClick={handleNext} colorScheme="teal">
              Next
            </Button>
          )}
        </HStack>
      </VStack>
    </Container>
  );
};

export default LearnReact;
